import React from 'react'
import ReactDOM from 'react-dom'
import { EDITOR_CONTENT } from 'components/Layout/NoteView'
import { connect } from 'react-redux'
import scrollIntoView from 'scroll-into-view'
import throttle from 'lodash/throttle'

import { getHeaderDOMElementFromId } from '@slite/editor/lib/utils/getHeaderDOMElement'

import { selectors as UISelectors } from 'data/ui'

import Tree from './OutlineTree'

import { NOTE_TITLE_ID } from 'components/Notes/Title'
import getHeadersStateFromEditor from '../utils/getHeadersStateFromEditor'

const OFFSET_TOP_EDITOR = 70

export default connect(function mapStateToProps(state) {
  return {
    isOutlineOpen: UISelectors.isOutlineOpen(state),
    outlineContainerId: UISelectors.getOutlineContainerId(state),
  }
})(function({ isOutlineOpen, outlineContainerId, editor, pluginStore }) {
  const [outlineContainer, setOutlineContainer] = React.useState(null)

  React.useEffect(
    () => {
      if (outlineContainerId) {
        setOutlineContainer(document.getElementById(outlineContainerId))
      }
      return () => {
        setOutlineContainer(null)
      }
    },
    [outlineContainerId]
  )

  if (!outlineContainerId || !isOutlineOpen || !outlineContainer) {
    return false
  }

  return (
    <OutlinePortal
      portalContainer={outlineContainer}
      editor={editor}
      pluginStore={pluginStore}
    />
  )
})

function OutlinePortal({ editor, pluginStore, portalContainer }) {
  const [activeHeaderId, setActiveHeaderId] = React.useState(null)
  const [headers, setHeaders] = React.useState([])

  React.useEffect(
    () => {
      function updateHeaders() {
        setHeaders(getHeadersStateFromEditor(editor))
      }

      updateHeaders()
      pluginStore.subscribeToItem('changes', updateHeaders)
      return function() {
        pluginStore.unsubscribeFromItem('changes', updateHeaders)
      }
    },
    [editor, pluginStore]
  )

  React.useEffect(
    () => {
      const updateHeadersOffsets = throttle(() => {
        const headersWithOffset = injectHeaderOffset(editor, headers)
        setActiveHeaderId(findActiveBlockId(headersWithOffset))
      }, 200)

      updateHeadersOffsets()
      const noteContentElement = document.getElementById(EDITOR_CONTENT)
      noteContentElement.addEventListener('scroll', updateHeadersOffsets)
      return function() {
        noteContentElement.removeEventListener('scroll', updateHeadersOffsets)
      }
    },
    [editor, headers]
  )

  const scrollToHeader = React.useCallback(
    headerId => {
      const headerElement = getHeaderDOMElementFromId(editor, headerId)
      if (!headerElement) {
        return
      }
      scrollIntoView(headerElement, {
        align: {
          top: 0,
          topOffset: 0,
        },
      })
    },
    [editor]
  )

  const onBackTopTop = React.useCallback(() => {
    scrollIntoView(document.getElementById(NOTE_TITLE_ID), {
      align: {
        top: 0,
        topOffset: 0,
      },
    })
  }, [])

  return ReactDOM.createPortal(
    <Tree
      headers={headers}
      activeHeaderId={activeHeaderId}
      onHeaderClick={scrollToHeader}
      onBackTopTop={onBackTopTop}
    />,
    portalContainer
  )
}

function injectHeaderOffset(editor, headers) {
  return headers.reduce((headers, header) => {
    const offset = getElementOffsetTop(editor, header.id)
    return [
      ...headers,
      {
        ...header,
        offset,
      },
    ]
  }, [])
}

function getElementOffsetTop(editor, id) {
  const elementBlock = getHeaderDOMElementFromId(editor, id)

  if (!elementBlock) {
    return NaN
  }
  const elementBoundingRectangle = elementBlock.getBoundingClientRect()
  const offset = elementBoundingRectangle.top - OFFSET_TOP_EDITOR
  return offset
}

function findActiveBlockId(headers) {
  let lastHeader = null
  for (const header of headers) {
    if (header.offset < 0) {
      lastHeader = header
    }
  }
  lastHeader = lastHeader || headers[0]
  return lastHeader && lastHeader.id
}
